/**
 * React's window onto this codebase's hand-rolled observers.
 *
 * Every store in SimFab already speaks the same dialect - `subscribe(fn)`
 * returning an unsubscribe, over a module-scoped listener set - so React does
 * not get a new state library; it gets an adapter. Two shapes cover all nine
 * stores:
 *
 *  - `useObservable` for stores with a real snapshot getter that returns a
 *    stable value between notifications (a string, a cached object).
 *  - `useTick` for pure "something changed" stores (journal, missions, the
 *    carrier), where the caller then reads the mutable source directly in
 *    render - the same freshness the imperative code has today.
 */

import { useState, useSyncExternalStore } from 'react'

export function useObservable<T>(
  subscribe: (fn: () => void) => () => void,
  getSnapshot: () => T,
): T {
  return useSyncExternalStore(subscribe, getSnapshot)
}

/**
 * A version counter that increments on every notification. The counter lives
 * per hook so getSnapshot is referentially stable between notifications,
 * which is what keeps useSyncExternalStore from looping under StrictMode.
 * The subscribe function is captured on first render - fine for the module
 * singletons this app subscribes to, wrong for anything shorter-lived.
 */
export function useTick(subscribe: (fn: () => void) => () => void): number {
  const [store] = useState(() => {
    let version = 0
    return {
      subscribe: (notify: () => void) =>
        subscribe(() => {
          version++
          notify()
        }),
      get: () => version,
    }
  })
  return useSyncExternalStore(store.subscribe, store.get)
}
